'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useQuery } from '@tanstack/react-query';
import { Heading } from './heading';

import { LiaArrowRightSolid } from 'react-icons/lia';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function CategoriesList({ locale }: { locale: string }) {
  const { t } = useTranslations();
  const { data: categories = { data: [] } } = useQuery({
    queryKey: [CacheKey.CATEGORIES, locale],
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          childs: true,
          image: true,
          where: { parent: { equals: 1 } },
        },
      }),
  });

  const categoriesByUsage = categories.data.filter(category =>
    category.short_description.includes(locale === 'sk' ? 'Podľa použitia' : 'According to use'),
  );

  const categoriesByMaterial = categories.data.filter(category =>
    category.short_description.includes(
      locale === 'sk' ? 'Podľa materi&aacute;lu' : 'According to the material',
    ),
  );

  return (
    <div
      className={cn(
        'custom-container',
        'grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:gap-12',
      )}
    >
      <div className="col-span-1 flex w-full flex-col items-start justify-start gap-6 border border-border bg-white p-6 sm:gap-8 sm:p-8 lg:gap-10 lg:p-10 xl:gap-12 xl:p-12">
        <Heading subtitle={t('text1244')} title={t('text1245')} />
        <div className="flex w-full flex-col items-start justify-start gap-0">
          {categoriesByUsage.map(category => (
            <Link
              prefetch={false}
              href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
              key={category.slug}
              className={cn(
                'group transition-all',
                'flex w-full items-center justify-between p-3 hover:bg-primary sm:p-4 lg:p-5 xl:p-6',
              )}
            >
              <div className="flex items-center justify-start gap-3">
                {category?.icon?.url && (
                  <Image
                    src={category.icon.url}
                    alt={resolveImageAlt(category?.icon, category?.name)}
                    width={0}
                    height={0}
                    className={cn('h-6 w-6 lg:h-8 lg:w-8', 'ea-categories-list__icon', 'svg-icon')}
                  />
                )}
                <h5
                  className={cn(
                    'font-heading uppercase',
                    'text-lg font-medium text-foreground transition group-hover:text-white sm:text-xl lg:text-2xl',
                  )}
                >
                  {category.name}
                </h5>
              </div>
              <LiaArrowRightSolid className="h-6 w-6 text-white lg:h-8 lg:w-8" />
            </Link>
          ))}
        </div>
      </div>
      <div className="col-span-1 flex w-full flex-col items-start justify-start gap-6 border border-border bg-white p-6 sm:gap-8 sm:p-8 lg:gap-10 lg:p-10 xl:gap-12 xl:p-12">
        <Heading subtitle={t('text1244')} title={t('text1246')} />
        <div className="flex w-full flex-col items-start justify-start gap-0">
          {categoriesByMaterial.map(category => (
            <Link
              prefetch={false}
              href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
              key={category.slug}
              className={cn(
                'group transition-all',
                'flex w-full items-center justify-between p-3 hover:bg-primary sm:p-4 lg:p-5 xl:p-6',
              )}
            >
              <div className="flex items-center justify-start gap-3">
                {category?.icon?.url && (
                  <Image
                    src={category.icon.url}
                    alt={resolveImageAlt(category?.icon, category?.name)}
                    width={0}
                    height={0}
                    className={cn('h-6 w-6 lg:h-8 lg:w-8', 'ea-categories-list__icon', 'svg-icon')}
                  />
                )}

                <h5
                  className={cn(
                    'font-heading uppercase',
                    'text-lg font-medium text-foreground transition group-hover:text-white sm:text-xl lg:text-2xl',
                  )}
                >
                  {category.name}
                </h5>
              </div>
              <LiaArrowRightSolid className="h-6 w-6 text-white lg:h-8 lg:w-8" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
